import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import LeadCapturePopup from './components/LeadCapturePopup/LeadCapturePopup';
import ZendeskWidget from './components/ZendeskWidget/ZendeskWidget';
import useLeadPopupTrigger from './hooks/useLeadPopupTrigger';
import { useAppSelector, useAppDispatch } from './store/hooks';
import { openModal, closeModal } from './store/slices/uiSlice';
import { fetchCountries, fetchNationalities, fetchTimezones } from './store/slices/utilitySlice';
import { clear2FAState } from './store/slices/authSlice';
import { setAuthHeader } from './services/api';
import { useSessionExpiration } from './hooks/useSessionExpiration';
import { startLiveTabHeartbeat, stopLiveTabHeartbeat } from './utils/tabSession';
import { NotificationProvider } from './components/NotificationSystem/NotificationSystem';
import { CurrencyProvider } from './context/CurrencyContext';
import { Toaster } from 'react-hot-toast';
import { JetSetterRoute, AdminRoute, AirlinerRoute, AuthenticatedRoute } from './components/ProtectedRoute/ProtectedRoute';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Modal from './components/Modal/Modal';
import { GlobalLoader } from './components/Loader';
import LoginForm from './components/Auth/LoginForm';
import EmailVerification from './components/Auth/EmailVerification';
import CompleteProfile from './components/Auth/CompleteProfile';
import ForgotPasswordForm from './components/Auth/ForgotPasswordForm';
import SessionExpiredNoticeModal from './components/Auth/SessionExpiredNoticeModal';
import {
  hasSessionExpiredNoticePending,
  consumeSessionExpiredNoticePending,
  clearSessionExpiredNoticePending,
  SHOW_SESSION_EXPIRED_NOTICE_EVENT
} from './utils/sessionExpiredNotice';
import { clearAuth } from './store/slices/authSlice';
import HomePage from './pages/HomePage/HomePage';
import SearchFlightPage from './pages/SearchFlightPage/SearchFlightPage';
import HowItWorksPage from './pages/HowItWorksPage/HowItWorksPage';
import HowItWorksMenuPage from './pages/HowItWorksMenuPage/HowItWorksMenuPage';
import WhatWeDoPage from './pages/WhatWeDoPage/WhatWeDoPage';
import WhoWeArePage from './pages/WhoWeArePage/WhoWeArePage';
import AboutUsPage from './pages/AboutUsPage/AboutUsPage';
import WaysToFlyPage from './pages/WaysToFlyPage/WaysToFlyPage';
import FAQPage from './pages/FAQPage/FAQPage';
import BlogPage from './pages/BlogPage/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage/BlogDetailPage';
import ContactUsPage from './pages/ContactUsPage/ContactUsPage';
import InvestorRelationsPage from './pages/InvestorRelationsPage/InvestorRelationsPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage/TermsAndConditionsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage/PrivacyPolicyPage';
import RefundCancellationPage from './pages/RefundCancellationPage/RefundCancellationPage';
import SafetySecurityPage from './pages/SafetySecurityPage/SafetySecurityPage';
import CookiePolicyPage from './pages/CookiePolicyPage/CookiePolicyPage';
import NewsPage from './pages/NewsPage/NewsPage';
import FlightSelectionPage from './pages/FlightSelectionPage/FlightSelectionPage';
import FlightDetailsPage from './pages/FlightDetailsPage/FlightDetailsPage';
import FlightBookingDetailsPage from './pages/FlightBookingDetailsPage/FlightBookingDetailsPage';
import FlightBookingFlowPage from './pages/FlightBookingFlowPage/FlightBookingFlowPage';
import AddPassengersPage from './pages/AddPassengersPage/AddPassengersPage';
import PaymentPage from './pages/PaymentPage/PaymentPage';
import PaymentCallbackPage from './pages/PaymentCallbackPage/PaymentCallbackPage';
import BookingSuccessPage from './pages/BookingSuccessPage/BookingSuccessPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage/ResetPasswordPage';
import TwoFactorSetupPage from './pages/TwoFactorSetupPage/TwoFactorSetupPage';
import AdminDashboard from './pages/AdminDashboard/AdminDashboard';
import CheckInPage from './pages/CheckInPage/CheckInPage';
import SelectArrivalAirportPage from './pages/SelectArrivalAirportPage/SelectArrivalAirportPage';
import ChangePaymentMethodPage from './pages/ChangePaymentMethodPage/ChangePaymentMethodPage';
import NotFoundPage from './pages/NotFoundPage/NotFoundPage';
import ChatPage from './pages/ChatPage';
import WelcomePage from './pages/WelcomePage/WelcomePage';
import InvoicePage from './pages/InvoicePage/InvoicePage';
import TwoFactorAuthPage from './pages/TwoFactorAuthPage/TwoFactorAuthPage';
import PasswordExpiredPage from './pages/PasswordExpiredPage/PasswordExpiredPage';
import FlightAllDetailsPage from './pages/FlightAllDetailsPage/FlightAllDetailsPage';
import AllFlightsPage from './pages/AllFlightsPage/AllFlightsPage';
import AllFlightDetailsPage from './pages/AllFlightDetailsPage/AllFlightDetailsPage';
import FlightBookingConfirmation from './components/FlightBookingConfirmation/FlightBookingConfirmation';
import AdminLoginPage from './pages/AdminDashboard/auth/AdminLoginPage';
import AdminRegisterPage from './pages/AdminDashboard/auth/AdminRegisterPage';
import AdminTwoFactorAuthPage from './pages/AdminDashboard/auth/AdminTwoFactorAuthPage';
import AdminForgotPasswordPage from './pages/AdminDashboard/auth/AdminForgotPasswordPage';
import AdminResetPasswordPage from './pages/AdminDashboard/auth/AdminResetPasswordPage';
import AdminPasswordExpiredPage from './pages/AdminDashboard/auth/AdminPasswordExpiredPage';
import AdminPaymentFailurePage from './pages/AdminDashboard/auth/AdminPaymentFailurePage';
import AdminPaymentSuccessPage from './pages/AdminDashboard/auth/AdminPaymentSuccessPage';
import AirlinerLoginPage from './airliner/pages/auth/AirlinerLoginPage';
import AirlinerRegisterPage from './airliner/pages/auth/AirlinerRegisterPage';
import AirlinerTwoFactorAuthPage from './airliner/pages/auth/AirlinerTwoFactorAuthPage';
import AirlinerPasswordExpiredPage from './airliner/pages/auth/AirlinerPasswordExpiredPage';
import AirlinerEmailVerificationPage from './airliner/pages/auth/AirlinerEmailVerificationPage';
import AirlinerOTPVerificationPage from './airliner/pages/auth/AirlinerOTPVerificationPage';
import AirlinerSMSOTPVerificationPage from './airliner/pages/auth/AirlinerSMSOTPVerificationPage';
import AirlinerCompleteProfilePage from './airliner/pages/auth/AirlinerCompleteProfilePage';
import AirlinerConfirmPasswordPage from './airliner/pages/auth/AirlinerConfirmPasswordPage';
import AirlinerRegistrationSuccessPage from './airliner/pages/auth/AirlinerRegistrationSuccessPage';
import JetSetterDashboard from './jetSetter/JetSetterDashboard';
import BookingDetailsPage from './jetSetter/pages/BookingDetails/BookingDetailsPage';
import AirlinerDashboard from './airliner/pages/AircraftDashboard/AircraftDashboard';
import SignUpForm from './components/Auth/SignUpForm';
import CookieConsent from './components/CookieConsent/CookieConsent'
import TwoFactorVerifyPage from './pages/TwoFactorVerifyPage/TwoFactorVerifyPage';
import { usePushNotifications } from './hooks/usePushNotifications';
import BrowserPushPrompt from './components/BrowserPushPrompt/BrowserPushPrompt';
import useSuspiciousActivity from './hooks/useSuspiciousActivity';
import SuspiciousActivityModal from './components/Security/SuspiciousActivityModal';

const IDLE_SESSION_TIMEOUT_MS = 15 * 60 * 1000;

// Import cookie consent debug helpers (only in development)
if (process.env.NODE_ENV === 'development') {
  import('./utils/cookieConsentHelpers');
}

function AppContent() {
  const dispatch = useAppDispatch();
  const { modals } = useAppSelector((state) => state.ui);
  const { twofa_type, isAuthenticated } = useAppSelector((state) => state.auth);
  const location = useLocation();

  // Sync FCM web push with browser Notification.permission while logged in
  usePushNotifications();

  // Fetch pending suspicious activity on app/session bootstrap (JetSetter + Airliner)
  useSuspiciousActivity();

  // Lead capture popup trigger (time, scroll, exit intent)
  const {
    isVisible: isLeadPopupVisible,
    triggerType: leadPopupTriggerType,
    onClose: closeLeadPopup,
    onDontShowAgain: dontShowLeadPopup,
    onSubscribed: onLeadSubscribed
  } = useLeadPopupTrigger();

  // Initialize session expiration monitoring
  useSessionExpiration();

  // End authenticated sessions after 15 minutes without user activity.
  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    let lastActivityAt = Date.now();
    let idleTimer;
    let expired = false;

    const expireIdleSession = () => {
      if (expired) return;
      expired = true;
      window.dispatchEvent(new CustomEvent('sessionExpired', {
        detail: { reason: 'idle_timeout' }
      }));
    };

    const scheduleIdleExpiry = () => {
      clearTimeout(idleTimer);
      const remaining = IDLE_SESSION_TIMEOUT_MS - (Date.now() - lastActivityAt);
      if (remaining <= 0) {
        expireIdleSession();
        return;
      }
      idleTimer = setTimeout(expireIdleSession, remaining);
    };

    const handleActivity = () => {
      const now = Date.now();
      // Avoid continuously recreating timers for high-frequency pointer events.
      if (now - lastActivityAt < 1000) return;
      lastActivityAt = now;
      scheduleIdleExpiry();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivityAt >= IDLE_SESSION_TIMEOUT_MS) {
        expireIdleSession();
      } else {
        handleActivity();
      }
    };

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    scheduleIdleExpiry();

    return () => {
      clearTimeout(idleTimer);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  // Signal this tab is active so new tabs do not auto-restore Remember Me
  useEffect(() => {
    if (isAuthenticated) {
      startLiveTabHeartbeat();
    } else {
      stopLiveTabHeartbeat();
    }
    return () => stopLiveTabHeartbeat();
  }, [isAuthenticated]);

  // Show session-expired notice after full-page reload logout (flag cleared on OK)
  useEffect(() => {
    if (hasSessionExpiredNoticePending()) {
      dispatch(openModal('sessionExpired'));
    }
  }, [dispatch]);

  // Open session-expired notice without importing store from utils (avoids circular deps)
  useEffect(() => {
    const handleShowSessionExpiredNotice = (event) => {
      const clearSession = event.detail?.clearSession !== false;
      if (clearSession) {
        dispatch(clearAuth());
      }
      dispatch(openModal('sessionExpired'));
    };

    window.addEventListener(SHOW_SESSION_EXPIRED_NOTICE_EVENT, handleShowSessionExpiredNotice);
    return () => {
      window.removeEventListener(SHOW_SESSION_EXPIRED_NOTICE_EVENT, handleShowSessionExpiredNotice);
    };
  }, [dispatch]);

  // Note: Google Analytics and other tracking scripts are now loaded
  // conditionally via CookieConsentBanner after user consent

  // Initialize auth token on app load
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      console.log('🔐 App Init: Setting auth header from sessionStorage');
      setAuthHeader(token);
    } else {
      console.log('🔐 App Init: No token found in sessionStorage');
    }
  }, []);

  // Load utility data on app initialization
  useEffect(() => {  
    dispatch(fetchCountries());
    dispatch(fetchNationalities());
    dispatch(fetchTimezones());
  }, [dispatch]);

  // Clear 2FA state when navigating away from 2FA page
  useEffect(() => {
    if (!location.pathname.includes('/2fa-verify') && twofa_type) {
      // We're leaving the 2FA page with 2FA state still active
      // This shouldn't happen in normal flow, but clear it just in case
      dispatch(clear2FAState());
    }
  }, [location.pathname, twofa_type, dispatch]);

  // Hide header and footer on dashboards
  const isDashboard = location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/airliner') ||
    location.pathname.startsWith('/jetsetter') ||
    location.pathname.startsWith('/admin');

  const handleOpenModal = (modalType) => {
    // Close login modal when opening forgot password or signup
    if (modalType === 'forgotPassword' || modalType === 'signup') {
      dispatch(closeModal('login'));
    }
    // Close forgot password modal when opening login
    if (modalType === 'login') {
      dispatch(closeModal('forgotPassword'));
      dispatch(closeModal('signup'));
      dispatch(closeModal('sessionExpired'));
      clearSessionExpiredNoticePending();
    }
    // Close signup modal when opening login
    if (modalType === 'login') {
      dispatch(closeModal('signup'));
    }
    dispatch(openModal(modalType));
  };

  const handleCloseModal = (modalType) => {
    dispatch(closeModal(modalType));
  };

  const handleSessionExpiredConfirm = () => {
    consumeSessionExpiredNoticePending();
    dispatch(closeModal('sessionExpired'));
    handleOpenModal('login');
  };

  return (
    <div className="min-h-screen flex flex-col bg-jet-gray-100">
      {!isDashboard && <Header onLoginClick={() => handleOpenModal('login')} />}

      {/* Zendesk Live Chat Widget */}
      <ZendeskWidget />

      {/* React Hot Toast */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            style: {
              background: '#10B981',
            },
          },
          error: {
            duration: 5000,
            style: {
              background: '#EF4444',
            },
          },
        }}
      />

      <main className={`flex-1 ${location.pathname === '/search-flight' ? 'flex-none' : ''}`}>
        <Routes>
          {/* Home page - accessible to non-logged users and JetSetter only */}
          <Route path="/" element={<HomePage />} />
          <Route path="/search-flight" element={<SearchFlightPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/what-we-do" element={<WhatWeDoPage />} />
          <Route path="/who-we-are" element={<WhoWeArePage />} />
          <Route path="/about-us" element={<AboutUsPage />} />
          <Route path="/ways-to-fly" element={<WaysToFlyPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogDetailPage />} />
          <Route path="/blogs/:id" element={<BlogDetailPage />} />
          <Route path="/contact-us" element={<ContactUsPage />} />
          <Route path="/investor-relations" element={<InvestorRelationsPage />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/refund-and-cancellation" element={<RefundCancellationPage />} />
          <Route path="/safety-and-security" element={<SafetySecurityPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/news" element={<NewsPage />} />

          {/* Protected Flight Booking Flow - Requires Authentication */}
          <Route path="/flight-list" element={
            <FlightSelectionPage />
          } />
          <Route path="/one-way-flight" element={
            <FlightSelectionPage />
          } />
          <Route path="/return-flight" element={
            <FlightSelectionPage />
          } />
          <Route path="/multi-city-search" element={
            <FlightSelectionPage />
          } />
          <Route path="/flight-details/:id" element={
            <AuthenticatedRoute>
              <FlightDetailsPage />
            </AuthenticatedRoute>
          } />
          <Route path="/flight-booking-details/:flightId" element={
            <AuthenticatedRoute>
              <FlightBookingDetailsPage />
            </AuthenticatedRoute>
          } />
          <Route path="/flight-booking-flow/:id" element={
            <AuthenticatedRoute>
              <FlightBookingFlowPage />
            </AuthenticatedRoute>
          } />
          <Route path="/add-passengers/:id" element={
            <AuthenticatedRoute>
              <AddPassengersPage />
            </AuthenticatedRoute>
          } />
          <Route path="/payment/:id" element={
            <AuthenticatedRoute>
              <PaymentPage />
            </AuthenticatedRoute>
          } />
          <Route path="/payment-callback" element={
            <AuthenticatedRoute>
              <PaymentCallbackPage />
            </AuthenticatedRoute>
          } />
          <Route path="/booking-success" element={
            <AuthenticatedRoute>
              <BookingSuccessPage />
            </AuthenticatedRoute>
          } />
          <Route path="/booking-confirmation/:bookingId" element={
            <AuthenticatedRoute>
              <FlightBookingConfirmation />
            </AuthenticatedRoute>
          } />

          {/* Authentication Routes */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/email-verify/:token" element={<EmailVerification />} />
          <Route path="/email-Verify/:token" element={<EmailVerification />} />
          <Route path="/complete-profile" element={<CompleteProfile />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/setup-2fa" element={<TwoFactorSetupPage />} />
          <Route path="/2fa-verify" element={<TwoFactorVerifyPage />} />
          <Route path="/twofauth/:post/:pass" element={<TwoFactorAuthPage />} />
          <Route path="/password/expired" element={<PasswordExpiredPage />} />

          {/* Protected Multi-City Booking Flow - Requires Authentication */}

          {/* Protected Flight Search and Details - Requires Authentication */}
          <Route path="/all-flights" element={
            <AuthenticatedRoute>
              <AllFlightsPage />
            </AuthenticatedRoute>
          } />
          <Route path="/all-flight-details/:id" element={
            <AuthenticatedRoute>
              <AllFlightDetailsPage />
            </AuthenticatedRoute>
          } />
          <Route path="/flight-all-details" element={
            <AuthenticatedRoute>
              <FlightAllDetailsPage />
            </AuthenticatedRoute>
          } />
          <Route path="/flightAllDetails" element={
            <AuthenticatedRoute>
              <BookingDetailsPage />
            </AuthenticatedRoute>
          } />

          {/* Protected User-Specific Pages - Requires Authentication */}
          <Route path="/check-in" element={
            <AuthenticatedRoute>
              <CheckInPage />
            </AuthenticatedRoute>
          } />
          <Route path="/select-arrival-airport/:id" element={
            <AuthenticatedRoute>
              <SelectArrivalAirportPage />
            </AuthenticatedRoute>
          } />
          <Route path="/chat" element={
            <AuthenticatedRoute>
              <ChatPage />
            </AuthenticatedRoute>
          } />
          <Route path="/welcome" element={
            <AuthenticatedRoute>
              <WelcomePage />
            </AuthenticatedRoute>
          } />
          <Route path="/change-payment-method" element={
            <AuthenticatedRoute>
              <ChangePaymentMethodPage />
            </AuthenticatedRoute>
          } />
          <Route path="/invoice/:bookingId" element={
            <AuthenticatedRoute>
              <InvoicePage />
            </AuthenticatedRoute>
          } />

          {/* Admin Authentication Routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/register" element={<AdminRegisterPage />} />
          <Route path="/admin/twofauth/:post/:pass" element={<AdminTwoFactorAuthPage />} />
          <Route path="/admin/password/forget" element={<AdminForgotPasswordPage />} />
          <Route path="/admin/password/reset/:token" element={<AdminResetPasswordPage />} />
          <Route path="/admin/password/expired" element={<AdminPasswordExpiredPage />} />
          <Route path="/admin/payment/failure" element={<AdminPaymentFailurePage />} />
          <Route path="/admin/payment/success" element={<AdminPaymentSuccessPage />} />

          {/* Airliner Authentication Routes */}
          <Route path="/airliner/auth/login" element={<AirlinerLoginPage />} />
          <Route path="/airliner/auth/register" element={<AirlinerRegisterPage />} />
          <Route path="/airliner/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/airliner/auth/reset-password" element={<ResetPasswordPage />} />
          <Route path="/airliner/auth/twofauth/:post/:pass" element={<AirlinerTwoFactorAuthPage />} />
          <Route path="/airliner/auth/password/expired" element={<AirlinerPasswordExpiredPage />} />
          <Route path="/airliner/auth/verify/:token" element={<AirlinerEmailVerificationPage />} />
          <Route path="/airliner/auth/verify-otp" element={<AirlinerOTPVerificationPage />} />
          <Route path="/airliner/auth/verify-sms-otp" element={<AirlinerSMSOTPVerificationPage />} />
          <Route path="/airliner/auth/complete-profile" element={<AirlinerCompleteProfilePage />} />
          <Route path="/airliner/auth/confirm-password" element={<AirlinerConfirmPasswordPage />} />
          <Route path="/airliner/auth/registration-success" element={<AirlinerRegistrationSuccessPage />} />

          {/* 404 Page - Should be last */}
          <Route path="*" element={<NotFoundPage />} />

          {/* Protected Dashboard Routes */}
          <Route path="/admin/*" element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } />

          <Route path="/jetsetter/*" element={
            <JetSetterRoute>
              <JetSetterDashboard />
            </JetSetterRoute>
          } />

          <Route path="/airliner/*" element={
            <AirlinerRoute>
              <AirlinerDashboard />
            </AirlinerRoute>
          } />

          {/* Protected Legacy profile routes - redirect to JetSetter dashboard */}
          <Route path="/profile" element={
            <JetSetterRoute>
              <Navigate to="/jetsetter/profile/user-details" replace />
            </JetSetterRoute>
          } />
          <Route path="/profile/user-details" element={
            <JetSetterRoute>
              <Navigate to="/jetsetter/profile/user-details" replace />
            </JetSetterRoute>
          } />
          <Route path="/profile/change-password" element={
            <JetSetterRoute>
              <Navigate to="/jetsetter/profile/change-password" replace />
            </JetSetterRoute>
          } />
          <Route path="/profile/booking-history" element={
            <JetSetterRoute>
              <Navigate to="/jetsetter/profile/booking-history" replace />
            </JetSetterRoute>
          } />
          <Route path="/profile/activity-log" element={
            <JetSetterRoute>
              <Navigate to="/jetsetter/profile/activity-log" replace />
            </JetSetterRoute>
          } />
          <Route path="/profile/my-passengers" element={
            <JetSetterRoute>
              <Navigate to="/jetsetter/profile/my-passengers" replace />
            </JetSetterRoute>
          } />
          <Route path="/profile/messages" element={
            <JetSetterRoute>
              <Navigate to="/jetsetter/profile/messages" replace />
            </JetSetterRoute>
          } />
        </Routes>
      </main>

      {!isDashboard && (
        <div className="pt-4 pb-[1.3rem]">
          <div className="w-[86%] mx-auto">
            <div className="rounded-[10px] border-b border-[rgba(167,180,201,0.2)] bg-white px-[5px]">
              <Footer />
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <Modal isOpen={modals.login} onClose={() => handleCloseModal('login')}>
        <LoginForm
          onSignUpClick={() => handleOpenModal('signup')}
          onForgotPasswordClick={() => handleOpenModal('forgotPassword')}
          onClose={() => handleCloseModal('login')}
        />
      </Modal>

      <Modal isOpen={modals.signup} onClose={() => handleCloseModal('signup')}>
        <SignUpForm onClose={() => handleCloseModal('signup')} />
      </Modal>

      {/* Lead Capture Popup — independent from auth flow */}
      <LeadCapturePopup
        isOpen={isLeadPopupVisible}
        triggerType={leadPopupTriggerType}
        onClose={closeLeadPopup}
        onDontShowAgain={dontShowLeadPopup}
        onSubscribed={onLeadSubscribed}
      />

      <Modal isOpen={modals.forgotPassword} onClose={() => handleCloseModal('forgotPassword')}>
        <ForgotPasswordForm
          onBackToLogin={() => handleOpenModal('login')}
          onClose={() => handleCloseModal('forgotPassword')}
        />
      </Modal>

      <Modal
        isOpen={modals.sessionExpired}
        onClose={handleSessionExpiredConfirm}
      >
        <SessionExpiredNoticeModal onConfirm={handleSessionExpiredConfirm} />
      </Modal>

      {/* Global Loader */}
      <GlobalLoader />

      <SuspiciousActivityModal />

      <BrowserPushPrompt />
    </div>
  );
}

function App() {
  return (
    <NotificationProvider>
      <CurrencyProvider>
        <Router>
          <AppContent />
          <CookieConsent />
        </Router>
      </CurrencyProvider>
    </NotificationProvider>
  );
}

export default App;
